import { Link } from "react-router-dom";
import { ArrowRight, Play, Star, Quote, Instagram } from "lucide-react";
import { BRAND, SERVICES, WHY_US } from "@/data/portfolio";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";
import { publicIconicMoments, positionToObjectPosition } from "@/data/iconicMoments";

// Small static curated fallback — used ONLY when admin has selected
// nothing. Never pulls from wedding / pre-wedding / client albums.
const ICONIC_FALLBACK = [
  { imageUrl: "", alt: "", caption: "", position: "center", fit: "cover" },
];

export default function Home() {
  const { settings, weddings, films, testimonials, instagram } = useSite() || {};
  const heroImg = settings?.heroImage;
  const heroVideo = settings?.heroVideo;
  const introText = settings?.introText || "At Mangalfera Films, we capture weddings with emotion, elegance, and authenticity. From heartfelt rituals to timeless portraits — every moment, preserved beautifully.";
  const featured = (weddings || []).slice(0, 3);
  const filmList = (films || []).slice(0, 4);
  const igGrid = (instagram || []).slice(0, 8);

  // ── Iconic Moments: admin-curated ONLY ──────────────────────
  // Strictly from settings.homeIconicMoments, in admin slot order.
  // No weddings.flatMap, no album auto-pull. Falls back to a tiny
  // static set only when nothing is curated.
  const iconic = publicIconicMoments(settings?.homeIconicMoments);
  const iconicSlots = iconic.length > 0 ? iconic : ICONIC_FALLBACK.filter(s => s.imageUrl);

  return (
    <div data-testid="home-page" className="bg-[#070707] text-[#F7F1E8]">
      {/* HERO */}
      <section className="home-hero relative h-screen w-full overflow-hidden" data-testid="hero-section">
        {heroVideo ? (
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src={fileUrl(heroVideo)} />
          </video>
        ) : heroImg && (
          <img src={fileUrl(heroImg)} alt="Cinematic wedding" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        )}
        <div className="absolute inset-x-0 top-0 h-40 md:h-52 bg-gradient-to-b from-black/55 to-transparent pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-36 md:h-48 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" aria-hidden="true" />

        {/* Bottom row — minimal CTAs + scroll */}
        <div className="absolute bottom-10 left-0 right-0 px-6 z-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <Link to="/weddings" className="eyebrow text-[10px] tracking-[0.32em] text-[#F7F1E8] hover:text-[#C99A45] transition flex items-center gap-2" data-testid="hero-view-weddings">
              View Weddings <ArrowRight size={12} />
            </Link>
            <div className="eyebrow text-[10px] tracking-[0.45em] text-[#F7F1E8]/50 hidden md:block">Scroll</div>
            <Link to="/contact" className="eyebrow text-[10px] tracking-[0.32em] text-[#C99A45] hover:text-[#F7F1E8] transition flex items-center gap-2" data-testid="hero-enquire">
              Enquire Now <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO — editorial layout */}
      <section className="py-28 md:py-44 px-6 md:px-10 border-b border-[#C99A45]/10" data-testid="intro-section" data-gsap="reveal">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-3 lg:pt-3">
            <div className="eyebrow text-[10px] tracking-[0.4em] text-[#C99A45]">— Welcome</div>
            <div className="mt-4 eyebrow text-[10px] text-[#F7F1E8]/40">Studio Note · 001</div>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            <p className="font-serif-display text-2xl md:text-[2.1rem] leading-[1.5] tracking-[-0.005em] text-[#F7F1E8]/85">
              At <span className="text-[#C99A45]">Mangal Fera Films</span>, we capture weddings with{" "}
              <em className="text-[#F7F1E8]">emotion, elegance, and authenticity</em>. From heartfelt
              rituals to timeless portraits — every moment, preserved beautifully.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-px bg-[#C99A45]/60"></div>
              <div className="eyebrow text-[10px] text-[#C99A45]/80">Urvesh Thummar · Founder</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED STORIES */}
      <section className="py-24 md:py-32 px-6 md:px-10 border-t border-[#C99A45]/10" data-testid="featured-stories" data-gsap="reveal">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="eyebrow text-[#C99A45]">Featured</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-3">Wedding Stories</h2>
            </div>
            <Link to="/weddings" className="eyebrow text-[11px] text-[#C99A45] hover:underline" data-testid="featured-view-all">View All Stories →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10" data-gsap="stagger">
            {featured.map((s) => (
              <Link to={`/weddings/${s.slug}`} key={s.slug} data-testid={`featured-story-${s.slug}`} className="group block">
                <div className="image-frame aspect-[3/4] bg-[#151515] gsap-image-hover" data-gsap="image">
                  {s.cover ? (
                    <img src={fileUrl(s.cover)} alt={s.couple} className="w-full h-full cover-safe" loading="lazy" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#181818] to-[#070707]" aria-hidden="true" />
                  )}
                </div>
                <div className="mt-5">
                  <div className="eyebrow text-[#C99A45]/80 text-[10px]">{s.type} · {s.location}</div>
                  <h3 className="font-serif-display text-2xl md:text-3xl mt-2 group-hover:text-[#C99A45] transition">{s.couple}</h3>
                  <p className="text-sm text-[#F7F1E8]/70 mt-3 leading-relaxed">{s.story}</p>
                  <div className="mt-4 eyebrow text-[10px] text-[#F7F1E8] flex items-center gap-2 group-hover:text-[#C99A45]">
                    View Story <ArrowRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS — Iconic Moments Mosaic (admin-curated only) */}
      {iconicSlots.length > 0 && (
      <section className="py-24 md:py-32 px-2 md:px-3 bg-[#070707]" data-testid="highlights-section" data-gsap="reveal">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-12 px-6">
            <div className="eyebrow text-[#C99A45]">— The Archive</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3">
              Iconic <em className="text-[#C99A45]">Moments</em>
            </h2>
            <p className="text-[#F7F1E8]/60 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
              A curated mosaic of our favourite frames — every wedding, every emotion, every quiet pause.
            </p>
          </div>

          <div className="iconic-mosaic" data-testid="highlights-grid" data-gsap="stagger">
            {iconicSlots.map((s, i) => (
              <figure
                key={i}
                data-testid={`highlight-${i}`}
                className="image-frame relative bg-[#151515] m-0 gsap-image-hover"
                style={{ aspectRatio: "3/4" }}
              >
                <img
                  src={fileUrl(s.imageUrl)}
                  alt={s.alt || "Iconic moment"}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full"
                  style={{
                    objectFit: s.fit === "contain" ? "contain" : "cover",
                    objectPosition: positionToObjectPosition(s.position),
                  }}
                />
                {s.caption && (
                  <figcaption className="absolute bottom-0 inset-x-0 p-3 text-[10px] eyebrow text-[#F7F1E8] bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition">
                    {s.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/weddings" className="btn-outline-gold inline-flex" data-testid="highlights-view-all">
              View Full Portfolio <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* FILMS PREVIEW */}
      <section className="cinematic-hero relative py-24 md:py-32 px-6 md:px-10 overflow-hidden" data-testid="films-preview" data-gsap="reveal">
        {/* Cinematic backdrop: hero/film poster + dark overlay so the
            section never reads as an empty black block. */}
        <div className="absolute inset-0 -z-0">
          {heroVideo ? (
            <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-25">
              <source src={fileUrl(heroVideo)} />
            </video>
          ) : (filmList[0]?.thumbnail || heroImg) ? (
            <img src={fileUrl(filmList[0]?.thumbnail || heroImg)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" loading="lazy" data-gsap="image" />
          ) : null}
          <div className="hero-grain cinematic-grain" aria-hidden="true" />
          <div className="hero-overlay cinematic-overlay absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow text-[#C99A45]">Wedding Films</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3">Every story has a rhythm.</h2>
            <p className="mt-5 text-[#F7F1E8]/70 leading-relaxed">Our films are crafted to let you relive those moments again and again — the vows, the laughter, the quiet glances.</p>
          </div>

          {filmList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-gsap="stagger">
              {filmList.map((f, i) => (
                <Link to="/films" key={f.id || i} className="group cursor-pointer block" data-testid={`film-card-${i}`}>
                  <div className="image-frame aspect-[4/5] relative bg-[#151515] gsap-image-hover" data-gsap="image">
                    {f.thumbnail ? (
                      <img src={fileUrl(f.thumbnail)} alt={f.couple} className="w-full h-full cover-safe" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d]" />
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full border border-[#C99A45] flex items-center justify-center bg-black/40 group-hover:bg-[#C99A45] group-hover:text-black transition">
                        <Play size={18} className="ml-1" fill="currentColor" />
                      </div>
                    </div>
                    {f.length && <div className="absolute top-3 right-3 eyebrow text-[9px] text-[#F7F1E8] bg-black/40 px-2 py-1">{f.length}</div>}
                  </div>
                  <div className="mt-4">
                    <div className="font-serif-display text-xl">{f.couple}</div>
                    <div className="eyebrow text-[10px] text-[#C99A45]/80 mt-1">{f.location}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            // No films yet → premium poster panel, never an empty black band.
            <Link to="/films" className="group block relative border border-[#C99A45]/20 overflow-hidden" data-testid="films-empty-poster">
              <div className="aspect-[21/9] md:aspect-[21/7] bg-gradient-to-br from-[#161616] to-[#0b0b0b] flex flex-col items-center justify-center text-center px-6">
                <div className="w-16 h-16 rounded-full border border-[#C99A45] flex items-center justify-center bg-black/40 group-hover:bg-[#C99A45] group-hover:text-black transition">
                  <Play size={22} className="ml-1" fill="currentColor" />
                </div>
                <div className="font-serif-display text-2xl md:text-3xl mt-6 text-[#F7F1E8]">Our wedding films, coming soon</div>
                <div className="eyebrow text-[10px] text-[#C99A45]/80 mt-3">Explore the films page</div>
              </div>
            </Link>
          )}

          <div className="mt-12 flex justify-center">
            <Link to="/films" className="btn-outline-gold" data-testid="films-watch-all">
              Watch All Films <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 px-6 md:px-10 border-t border-[#C99A45]/10" data-testid="services-section" data-gsap="reveal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="eyebrow text-[#C99A45]">What We Offer</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3">Services</h2>
            <div className="gold-divider mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C99A45]/10 border border-[#C99A45]/10" data-gsap="stagger">
            {SERVICES.map((s, i) => (
              <div key={s} data-testid={`service-${i}`} className="bg-[#070707] hover:bg-[#0e0e0e] transition px-8 py-10 group">
                <div className="eyebrow text-[10px] text-[#C99A45]/70">{String(i+1).padStart(2, "0")}</div>
                <div className="font-serif-display text-2xl mt-3 group-hover:text-[#C99A45] transition">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-32 px-6 md:px-10 bg-[#0A0A0A]" data-testid="why-us-section" data-gsap="reveal">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-[#C99A45]">Why Choose Us</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3 leading-[1.05]">
              Why <em className="text-[#C99A45] not-italic">Mangal Fera</em>?
            </h2>
            <p className="mt-6 text-[#F7F1E8]/70 leading-relaxed">A decade of cinematic storytelling, a deep love for Gujarati traditions, and a team that treats your wedding like it's their own.</p>
            <ul className="mt-8 space-y-3 text-sm text-[#F7F1E8]/80">
              <li>· Candid and cinematic shooting style</li>
              <li>· Deep understanding of Gujarati & Indian weddings</li>
              <li>· Available across Gujarat, India & destinations</li>
            </ul>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-[#C99A45]/10 border border-[#C99A45]/10" data-gsap="stagger">
            {WHY_US.map((w, i) => (
              <div key={i} className="bg-[#0A0A0A] py-12 px-8" data-testid={`stat-${i}`}>
                <div className="font-serif-display text-6xl md:text-7xl text-[#C99A45]">{w.num}</div>
                <div className="eyebrow text-[10px] mt-3 text-[#F7F1E8]/70">{w.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 px-6 md:px-10" data-testid="testimonials-section" data-gsap="reveal">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="eyebrow text-[#C99A45]">Kind Words</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3">From Our Couples</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12" data-gsap="stagger">
            {(testimonials || []).slice(0, 3).map((t, i) => (
              <div key={t.id || i} className="border border-[#C99A45]/20 p-10" data-testid={`testimonial-${i}`}>
                <Quote className="text-[#C99A45]" size={28} />
                <p className="font-serif-display italic text-xl mt-6 leading-relaxed text-[#F7F1E8]/90">"{t.quote}"</p>
                <div className="mt-8 flex items-center gap-2 text-[#C99A45]">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} fill="currentColor" />)}
                </div>
                <div className="mt-4 eyebrow text-[10px] text-[#F7F1E8]/70">{t.name} · {t.place}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-24 md:py-32 px-6 md:px-10 border-t border-[#C99A45]/10" data-testid="instagram-section" data-gsap="reveal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="eyebrow text-[#C99A45]">Behind The Scenes</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-3">Follow Our Stories</h2>
            <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-[#C99A45] hover:underline eyebrow text-[10px]" data-testid="instagram-handle-link">
              <Instagram size={14} /> {BRAND.instagramHandle}
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3" data-gsap="stagger">
            {igGrid.map((it, i) => (
              <a key={it.id || i} href={it.link || BRAND.instagram} target="_blank" rel="noreferrer" className="image-frame aspect-square bg-[#151515] block gsap-image-hover" data-testid={`ig-tile-${i}`} data-gsap="image">
                {it.url ? (
                  <img src={fileUrl(it.url)} alt={`Instagram ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#181818] to-[#070707]" aria-hidden="true" />
                )}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 md:py-44 px-6 md:px-10 overflow-hidden" data-testid="final-cta" data-gsap="reveal">
        {featured[1]?.cover && (
          <img src={fileUrl(featured[1].cover)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/80 to-[#070707]" />
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="eyebrow text-[#C99A45]">Let's Begin</div>
          <h2 className="font-serif-display text-5xl md:text-7xl mt-4 leading-[1.05]">
            Your wedding deserves to be remembered <em className="text-[#C99A45]">beautifully</em>.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold" data-testid="final-cta-quote">
              Get Custom Quotation <ArrowRight size={14} />
            </Link>
            <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="final-cta-whatsapp">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
